// ptoServices.js — RTK Query hooks for the PTO API (thread #1360, Phase 3)
//
// All endpoints hit /pto/portal/... (mounted in Portal-BFF ptoRoute.js).
// Auth is handled by prepareHeaders (Bearer token from Redux auth state).

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";

export const ptoApi = createApi({
  reducerPath: "ptoApi",
  tagTypes: ["PtoBalance", "PtoRequests", "PtoManagerPending", "PtoAdminList"],
  keepUnusedDataFor: 60,
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: `${BACKEND_URL}/pto/portal`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth?.token;
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    // Employee: own balance for current year
    getMyPtoBalance: builder.query({
      query: (year) => ({ url: `/my-balance`, params: year ? { year } : {} }),
      providesTags: ["PtoBalance"],
    }),

    // Employee: own request list
    getMyPtoRequests: builder.query({
      query: ({ year, status } = {}) => ({
        url: `/my-requests`,
        params: { ...(year ? { year } : {}), ...(status ? { status } : {}) },
      }),
      providesTags: ["PtoRequests"],
    }),

    // Employee: submit a PTO request
    // body: { week, hours, note, employee_recid? (managers submitting for a tech) }
    submitPtoRequest: builder.mutation({
      query: (body) => ({ url: `/request`, method: "POST", body }),
      invalidatesTags: ["PtoBalance", "PtoRequests", "PtoManagerPending"],
    }),

    // Employee: withdraw your own request, while it is still pending a decision
    // (thread #1540 round 9). Identity-scoped server-side — the BFF checks the
    // request belongs to the caller, so there is no employee id in this call.
    withdrawPtoRequest: builder.mutation({
      query: ({ requestId, note }) => ({
        url: `/request/${requestId}/withdraw`,
        method: "POST",
        body: { note },
      }),
      // The balance moves on a withdrawal: the request stops reserving its hours
      // and they go back to available. The manager's pending list changes too.
      invalidatesTags: ["PtoBalance", "PtoRequests", "PtoManagerPending"],
    }),

    // Manager: pending requests for team
    getManagerPending: builder.query({
      query: (year) => ({ url: `/manager/pending`, params: year ? { year } : {} }),
      providesTags: ["PtoManagerPending"],
    }),

    // Manager: approve a request
    approvePtoRequest: builder.mutation({
      query: ({ requestId, note }) => ({
        url: `/manager/approve/${requestId}`,
        method: "POST",
        body: { note },
      }),
      invalidatesTags: ["PtoBalance", "PtoRequests", "PtoManagerPending", "PtoAdminList"],
    }),

    // Manager: deny a request
    denyPtoRequest: builder.mutation({
      query: ({ requestId, note }) => ({
        url: `/manager/deny/${requestId}`,
        method: "POST",
        body: { note },
      }),
      // PtoBalance is in this list because denying now RETURNS hours (thread
      // #1540 round 9). While only approved requests counted against a balance,
      // a denial changed nothing and invalidating the balance would have been
      // pointless; now a pending request holds its hours, so refusing it gives
      // them back. Same reason the employee's own list is invalidated.
      invalidatesTags: ["PtoBalance", "PtoRequests", "PtoManagerPending"],
    }),

    // PTO Admin (President): all employees + balances
    getPtoAdminEmployees: builder.query({
      query: (year) => ({ url: `/admin/employees`, params: year ? { year } : {} }),
      providesTags: ["PtoAdminList"],
    }),

    // PTO Admin (President): manual balance adjustment
    adjustPtoBalance: builder.mutation({
      query: (body) => ({ url: `/admin/adjust`, method: "POST", body }),
      invalidatesTags: ["PtoAdminList"],
    }),
  }),
});

export const {
  useGetMyPtoBalanceQuery,
  useGetMyPtoRequestsQuery,
  useSubmitPtoRequestMutation,
  useWithdrawPtoRequestMutation,
  useGetManagerPendingQuery,
  useApprovePtoRequestMutation,
  useDenyPtoRequestMutation,
  useGetPtoAdminEmployeesQuery,
  useAdjustPtoBalanceMutation,
} = ptoApi;
